import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "user", "client", "creator", "compliance-officer"];

export default function UsersTab({ companyId }) {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [updatingId, setUpdatingId] = useState(null);
    const { call: fetchDetails, isLoading } = useBluelagoon({ method: "getCompanyDetails" });
    const { call: updateRole } = useBluelagoon({ method: "updateUserRole" });

    const load = useCallback(() => {
        fetchDetails({ companyId, usersLimit: 500 })
            .then((res) => setUsers(res?.data?.users || []))
            .catch(() => setUsers([]));
    }, [fetchDetails, companyId]);

    useEffect(() => { load(); }, [load]);

    const handleRoleChange = async (userId, newRole) => {
        setUpdatingId(userId);
        const res = await updateRole({ userIdToUpdate: userId, newRole }).catch(() => null);
        setUpdatingId(null);
        if (res?.status === 200) {
            toast.success(t("admin.manageCompany.users.roleUpdated", "Role updated"));
            load();
        } else {
            toast.error(res?.message || t("admin.manageCompany.users.roleUpdateError", "Failed to update role"));
        }
    };

    if (isLoading && users.length === 0) {
        return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>{t("admin.manageCompany.users.email", "Email")}</TableHead>
                    <TableHead>{t("admin.manageCompany.users.name", "Name")}</TableHead>
                    <TableHead className="w-48">{t("admin.manageCompany.users.role", "Role")}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {users.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                        {t("admin.manageCompany.users.empty", "No users")}
                    </TableCell></TableRow>
                ) : users.map((u) => (
                    <TableRow key={u.id}>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell>{u.name || "—"}</TableCell>
                        <TableCell>
                            <Select
                                value={u.role || "user"}
                                onValueChange={(v) => handleRoleChange(u.id, v)}
                                disabled={updatingId === u.id}
                            >
                                <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map((r) => (
                                        <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
